import {Box, BoxProps, FlexProps, Grid, GridItem, GridProps, StackProps, useMultiStyleConfig} from '@chakra-ui/react'
import {CardPhotoVideo, CustomStyle} from '_uiComponents'
import React, {useEffect, useRef, useState} from 'react'
import {CardPhotoVideoObject} from '_types'
import {useAnalytics, useIsElementInViewport} from '_customHooks'
import {PAGE_DESIGNER_MODULES} from '_utils'
import {toEditorialAnalyticsList} from '_analytics/utils/analyticsUtils'

interface TwoThirdOneThirdProps {
    leftElement?: CardPhotoVideoObject
    rightElement?: CardPhotoVideoObject
    classes?: string
    extraCSS?: string
    backgroundColor?: string
    isBeforeRightMobile?: boolean
    isBeforeRightDesk?: boolean
    analyticsID?: string
    position: number
    analyticsList?: string
    analyticsName?: string
    /** Above-the-fold priority (first-row module), propagated to the video for eager load. */
    isMainBanner?: boolean
}

/**
 * @name TwoThirdOneThird
 *
 * @component
 *
 * @description
 * Renders a component that displays two elements side by side, with customizable layout and styling options.
 *
 * @param {TwoThirdOneThirdProps} props - The properties object adhering to the `TwoThirdOneThirdProps` interface.
 * @param {CardPhotoVideoObject} [props.leftElement] - An optional element to display on the left side.
 * @param {CardPhotoVideoObject} [props.rightElement] - An optional element to display on the right side.
 * @param {string} [props.classes] - Optional CSS class names to apply to the component for styling purposes.
 * @param {string} [props.extraCSS] - Optional additional CSS styles to apply to the component.
 * @param {string} [props.backgroundColor] - Optional background color for the component.
 * @param {boolean} [props.isBeforeRightMobile] - Optional boolean indicating if the left element should be positioned before the right element on mobile devices.
 * @param {boolean} [props.isBeforeRightDesk] - Optional boolean indicating if the left element should be positioned before the right element on desktop devices.
 * @param {string} [props.analyticsID] - Optional string for analytics tracking purposes.
 * @param {number} props.position - A required number indicating the position or order of the component in a sequence.
 * @param {string} [props.analyticsList] - PLP analyticsList (normalized to editorial for Page Designer).
 * @param {string} [props.analyticsName] - Optional analytics name for promo events.
 *
 * @returns {JSX.Element} A JSX element representing the `TwoThirdOneThird` layout.
 */
const TwoThirdOneThird: React.FC<TwoThirdOneThirdProps> = ({
    leftElement,
    rightElement,
    classes,
    extraCSS,
    backgroundColor,
    isBeforeRightMobile,
    isBeforeRightDesk,
    analyticsID,
    position,
    analyticsList,
    analyticsName,
    isMainBanner = false,
}) => {
    const styles = useMultiStyleConfig('TwoThirdOneThird', {backgroundColor, isBeforeRightMobile, isBeforeRightDesk}) as Record<
        string,
        StackProps & FlexProps & BoxProps & GridProps
    >
    const {analyticsEventPromoView, analyticsEventPromoClick} = useAnalytics()
    const targetElement = useRef(null)
    const isVisible = useIsElementInViewport(targetElement)
    const [alreadySent, setAlreadySent] = useState(false)
    const analyticsListEditorial = toEditorialAnalyticsList(analyticsList)

    const triggerAnalyticsEventPromoClick = (textBtn: string, positionBtn?: string): void => {
        analyticsEventPromoClick({
            id: analyticsID ? analyticsID : 'null',
            position: positionBtn ? String(position) + '-' + positionBtn : String(position),
            creative: PAGE_DESIGNER_MODULES.TWO_THIRDS_ONE_THIRD_CLOUDINARY,
            promoText: textBtn,
            name: analyticsName ? analyticsName : 'null',
        })
    }

    const buildSubItem = (positionItem: string, style: GridProps, element?: CardPhotoVideoObject) => {
        return (
            <GridItem {...style} data-testid={`two-third-one-third-${positionItem}`} className={`cc-object-container cc-object-${positionItem}`}>
                {element && (
                    <CardPhotoVideo
                        propsObject={{...element, layout: 'fullOneThird', isLeft: positionItem === 'left'}}
                        classes={classes}
                        isVisible={isVisible}
                        idProductCta={positionItem === 'left' ? '0' : '1'}
                        onClick={() => {
                            triggerAnalyticsEventPromoClick(element && element.ctaText ? element.ctaText : '', positionItem === 'left' ? '1' : '2')
                        }}
                        analyticsList={analyticsListEditorial}
                        position={position}
                        videoSlot={positionItem === 'left' ? 'twoThirdLarge' : 'twoThirdSmall'}
                        isPriority={isMainBanner}
                    />
                )}
            </GridItem>
        )
    }

    useEffect(() => {
        if (isVisible && !alreadySent) {
            analyticsEventPromoView({
                id: analyticsID ? analyticsID : 'null',
                position: position,
                creative: PAGE_DESIGNER_MODULES.TWO_THIRDS_ONE_THIRD_CLOUDINARY,
                name: analyticsName ? analyticsName : 'null',
            })
            setAlreadySent(true)
        }
    }, [isVisible])

    return (
        <Box {...styles.wrapper} data-testid="two-third-one-third" ref={targetElement}>
            <CustomStyle css={extraCSS} />
            <Grid {...styles.grid} className={`${classes ?? ''} cc-pd cc-pd-two-thirds-one-third`}>
                {buildSubItem('left', styles.gridItemLeft, leftElement)}
                {buildSubItem('right', styles.gridItemRight, rightElement)}
            </Grid>
        </Box>
    )
}

export {TwoThirdOneThird}
